/**
 * What the adjust-isolated-margin form may add or remove.
 *
 * Ported from `AdjustIsolatedMarginModal.helpers.ts`, with two changes.
 *
 * The arithmetic is exact: the shipped version ran on `number`, so a removable
 * figure was a float subtraction of two API strings.
 *
 * And "removable" now keeps the position's INITIAL margin at the current
 * leverage, not merely its maintenance margin. `isolated - maintenance` let a
 * trader strip a position down to the edge of liquidation with one click; what
 * may safely come out is whatever exceeds the margin the position would need to
 * be opened again as it stands.
 */
import { Dec, dec } from "../decimal";
import { MaxResult, maxOk, maxUnavailable } from "./types";

/** Isolated margin is a USD figure; cents are the finest grid it is shown on. */
const CENTS = 2;

export interface IsolatedAddMaxInput {
  /** The account's free collateral, in USD. */
  availableBalance?: Dec | null;
  usdcDecimals: number;
}

export interface IsolatedRemoveMaxInput {
  /** Margin currently posted against this position, in USD. */
  isolatedMargin?: Dec | null;
  /** Signed position size in the base asset; only its magnitude matters. */
  positionAmount?: Dec | null;
  markPrice?: Dec | null;
  /** The position's leverage. Zero or negative cannot size a margin. */
  leverage?: Dec | number | null;
  /** Unrealized PnL, in USD. A loss reduces what may come out; a gain does not. */
  unrealizedPnl?: Dec | null;
  usdcDecimals: number;
}

/** USD is quoted in cents, and never more finely than the collateral token is. */
const usdDecimals = (usdcDecimals: number): number =>
  Math.min(CENTS, Math.max(0, usdcDecimals));

const read = (value: Dec | number | null | undefined): Dec | null =>
  value === null || value === undefined || value === "" ? null : dec.parse(value);

export const isolatedAddMax = (input: IsolatedAddMaxInput): MaxResult => {
  const available = read(input.availableBalance);
  if (available === null) return maxUnavailable("balance_unknown");

  const max = dec.isNeg(available)
    ? "0"
    : dec.round(available, usdDecimals(input.usdcDecimals), "floor");
  return maxOk(max, "balance", "USDC");
};

export const isolatedRemoveMax = (input: IsolatedRemoveMaxInput): MaxResult => {
  const isolated = read(input.isolatedMargin);
  if (isolated === null) return maxUnavailable("balance_unknown");

  const amount = read(input.positionAmount);
  if (amount === null) return maxUnavailable("no_position");

  const mark = read(input.markPrice);
  if (mark === null) return maxUnavailable("price_unknown");

  const leverage = read(input.leverage);
  if (leverage === null || !dec.gt(leverage, "0")) {
    return maxUnavailable("leverage_unknown");
  }

  // An absent PnL is an unknown deduction, not a zero one: offering the margin
  // as if the position were flat is exactly the mistake this module exists to
  // stop.
  const pnl = read(input.unrealizedPnl);
  if (pnl === null) return maxUnavailable("balance_unknown");

  const size = dec.isNeg(amount) ? dec.sub("0", amount) : amount;
  const notional = dec.mul(size, mark);
  // Ceiled: a repeating requirement must be held back in full, never a cent short.
  const required = dec.div(notional, leverage, 18, "ceil");
  if (required === null) return maxUnavailable("leverage_unknown");

  const loss = dec.isNeg(pnl) ? pnl : "0";
  const removable = dec.sub(dec.add(isolated, loss), required);

  const max = dec.isNeg(removable)
    ? "0"
    : dec.round(removable, usdDecimals(input.usdcDecimals), "floor");
  return maxOk(max, "initial_margin", "USDC");
};
